/**
 * Utility for centralized error logging and handling
 * This allows for consistent error reporting across the application
 * and makes it easy to change logging behavior in one place.
 */

// Environment-based logging controls
const isDevelopment = process.env.NODE_ENV !== 'production';

/**
 * Log an error with contextual information
 * @param {string} context - The context where the error occurred (e.g., 'jobService.getJobs')
 * @param {Error|string} error - The error object or message
 * @param {Object} [additionalInfo] - Any additional information to include in the log
 */
export const logError = (context, error, additionalInfo = {}) => {
  // Only log in development or if explicitly enabled in production
  if (isDevelopment) {
    const errorMsg = error instanceof Error ? error.message : error;
    const errorObj = {
      context,
      message: errorMsg,
      timestamp: new Date().toISOString(),
      ...additionalInfo
    };
    
    // In development, show more detailed logs
    console.error(`[ERROR] ${context}:`, errorObj);
    
    // In the future, this could be extended to:
    // - Send errors to a monitoring service
    // - Log errors to a backend API
    // - Display user-friendly error messages
  }
};

/**
 * Format an error for consistent return values in service functions
 * @param {string} context - The context where the error occurred
 * @param {Error|string} error - The error object or message
 * @param {string} [defaultMsg] - Default message if none is provided
 * @returns {Object} Formatted error object with success: false
 */
export const formatError = (context, error, defaultMsg = 'An unexpected error occurred') => {
  // Log the error
  logError(context, error);
  
  // Determine the user-facing error message
  let errorMessage;
  
  if (error?.message?.includes('Failed to fetch') || 
      error?.message?.includes('NetworkError')) {
    errorMessage = 'Cannot connect to the server. Please check your network connection.';
  } else {
    errorMessage = (error instanceof Error) ? error.message : 
                   (typeof error === 'string') ? error : defaultMsg;
  }
  
  // Return a consistent error format
  return { 
    success: false, 
    error: errorMessage
  };
};

export default {
  logError,
  formatError
};
